import { useStore } from 'effector-react';
import { useCallback, useEffect, useMemo } from 'react';
import { CheckboxBase } from 'components/Checkbox';
import MultiSelect from 'components/form/MultiSelect';
import multiSelectStyles from 'components/form/MultiSelect/index.module.css';
import { OptionItemProps } from 'components/form/MultiSelect/OptionItem';
import DropdownButton from 'components/form/Select/DropdownButton';
import Icon from 'components/Icon';
import MenuItem from 'components/MenuItem';
import { AssetJson } from 'models/assets/dto';
import { getAssetsFx } from 'models/assets/effects';
import { assetsList } from 'models/assets/store';
import { onceFx } from 'models/modelUtils/onceFx';
import { FilterPropsBase, FilterValues } from '../model';
import styles from './index.module.css';

type LabelValue = AssetJson['labels'][number]['value'];

type Option = { id: LabelValue; name: LabelValue; disabled: boolean };

type Props = FilterPropsBase & {
	value: LabelValue[];
	onChange: (newValue: LabelValue[]) => void;
	values: Partial<FilterValues>;
};

function LabelValueFilter({
	defaultOpen,
	fixed,
	onChange,
	onClose,
	resetFilter,
	value,
	values,
}: Props) {
	const assetsListStore = useStore(assetsList);

	useEffect(() => {
		onceFx(getAssetsFx);
	}, []);

	const labelKeys = values?.labelKeys || [];

	const options = useMemo(() => {
		const result: {
			[key: string]: Option;
		} = {};

		for (const assetItem of assetsListStore) {
			if (assetItem.is_external) continue;

			for (const label of assetItem.labels) {
				result[label.value] = result[label.value] || {
					id: label.value,
					name: label.value,
					disabled: true,
				};

				// There's a corner case for flipping disabled flag, EVEN if we have processed that label value before:
				//		labelKeys = ['surname'];
				//		assetItem.labels = [{ key: 'lastname', value: 'Smith' }, { key: 'surname', value: 'Smith'}]
				if (labelKeys.length === 0 || labelKeys.includes(label.key)) {
					result[label.value].disabled = false;
				}
			}
		}

		return Object.values(result)
			.filter((option) => !option.disabled || value.includes(option.id)) // Must show previously checked value, even if disabled.
			.sort((a, b) => a.name.localeCompare(b.name))
			.sort((a, b) => Number(a.disabled) - Number(b.disabled));
	}, [assetsListStore, labelKeys]);

	const selected = useMemo(() => {
		return options.filter((val) => value.includes(val.name));
	}, [value, options]);

	function handleChange(newSelected: Option[]) {
		onChange(newSelected.map((option) => option.name));
	}

	const dropdownButton = useCallback(
		(dropdownProps) => <DropdownButton {...dropdownProps} fixed={fixed} onClose={resetFilter} />,
		[fixed, resetFilter]
	);

	return (
		<>
			{labelKeys.length > 0 && <Icon name="Link/Regular" size={20} className={styles.linkIcon} />}

			<MultiSelect
				defaultOpen={defaultOpen}
				label={{ primary: 'Label values', secondary: `${selected.length || ''}` }}
				options={options}
				value={selected}
				onChange={handleChange}
				onClose={onClose}
				render={{ dropdownButton, optionItem: OptionItem }}
				hasSearch
				hasApplyButton
			/>
		</>
	);
}

function OptionItem(props: OptionItemProps<Option>) {
	const { option, selected, searchString, onClick } = props;

	const optionText = option.name;

	const isSearched = searchString
		? optionText.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
		: true;

	return isSearched ? (
		<MenuItem size="small" onClick={onClick} disabled={!!option.disabled}>
			<CheckboxBase checked={selected} className={multiSelectStyles.checkbox} />
			{optionText}
		</MenuItem>
	) : null;
}

export default LabelValueFilter;
